import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';
import { IMG } from '../lib/images';
import { SEO } from '../components/SEO';

export default function CollectionsPage() {
  const { t } = useI18n();
  const categories = t('collections.categories');
  const imgMap = {
    femme: IMG.catFemme,
    homme: IMG.catHomme,
    chaussant: IMG.sandals,
    accessoires: IMG.catAccessories,
  };

  return (
    <main className="bg-background">
      <SEO
        title={t('collections.title')}
        description="Découvrez les collections Tahitian Move — prêt-à-porter femme et homme, savates polynésiennes, accessoires lifestyle."
      />
      {/* Hero header */}
      <section className="relative pt-40 pb-20 md:pt-48 md:pb-24 bg-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="eyebrow">{t('collections.title')}</p>
          <h1 className="display text-6xl md:text-8xl lg:text-9xl mt-6 max-w-4xl">
            {t('collections.subtitle')}
          </h1>
          <p className="mt-10 text-ink-soft max-w-2xl text-base md:text-lg leading-relaxed">
            {t('collections.intro')}
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 space-y-20 md:space-y-32">
          {categories.map((cat, i) => (
            <article
              key={cat.key}
              data-testid={`collection-${cat.key}`}
              className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center ${
                i % 2 === 1 ? 'md:[direction:rtl]' : ''
              }`}
            >
              <div className="md:col-span-7 media-card aspect-[4/5] md:aspect-[5/4] md:[direction:ltr]">
                <img src={imgMap[cat.key]} alt={cat.title} className="w-full h-full object-cover" />
              </div>
              <div className="md:col-span-5 md:[direction:ltr]">
                <p className="font-serif text-7xl text-lagoon">0{i + 1}</p>
                <h2 className="display text-5xl md:text-6xl mt-4 text-ink">{cat.title}</h2>
                <p className="text-ink-soft mt-6 text-base md:text-lg leading-relaxed">{cat.body}</p>
                <Link to="/boutique" className="btn-outline mt-8">
                  {t('common.visitBoutique')} <ArrowUpRight size={14} />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Savates gallery — signature visual */}
      <section className="py-24 md:py-32 bg-ocean text-white overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-5">
            <p className="eyebrow text-lagoon">Signature</p>
            <h2 className="display text-4xl md:text-5xl lg:text-6xl mt-6">
              Nos savates, comme des œuvres.
            </h2>
            <p className="text-white/70 mt-8 text-base md:text-lg leading-relaxed">
              Chaque paire raconte une histoire — Teahupo’o, vagues, motifs polynésiens.
              Une iconographie qui fait de nos savates bien plus qu’un chaussant : un objet identitaire,
              porté en boutique comme exposé en cadre doré.
            </p>
          </div>
          <div className="md:col-span-7 media-card">
            <img src={IMG.savatesGallery} alt="Tahitian Move — savates en cadres dorés" className="w-full h-auto" />
          </div>
        </div>
      </section>

      {/* Lifestyle gallery — chaussant en situation */}
      <section className="py-24 md:py-32 bg-paper border-t border-sand">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 items-end">
            <div className="md:col-span-7">
              <p className="eyebrow">Galerie chaussant</p>
              <h2 className="display text-4xl md:text-5xl lg:text-6xl mt-6 text-ink">
                Le Pacifique, aux pieds.
              </h2>
            </div>
            <p className="md:col-span-5 text-ink-soft text-base md:text-lg leading-relaxed">
              De la plage aux pontons de Bora Bora, nos savates accompagnent chaque mouvement —
              parures de strass, motifs marquisiens, broderies florales, déclinaisons sur mesure.
            </p>
          </div>

          <div className="grid grid-cols-12 gap-3 md:gap-5">
            {/* Bento layout */}
            <div className="media-card col-span-12 md:col-span-7 md:row-span-2 aspect-[4/3] md:aspect-auto md:h-[680px]">
              <img src={IMG.chaussantGallery[0]} alt="Savates Tahitian Move — lifestyle" className="w-full h-full object-cover" />
            </div>
            <div className="media-card col-span-6 md:col-span-5 aspect-square">
              <img src={IMG.chaussantGallery[1]} alt="Savates Tahitian Move — motif marquisien" className="w-full h-full object-cover" />
            </div>
            <div className="media-card col-span-6 md:col-span-5 aspect-square">
              <img src={IMG.chaussantGallery[2]} alt="Savates Tahitian Move — strass Bora Bora" className="w-full h-full object-cover" />
            </div>
            <div className="media-card col-span-6 md:col-span-6 aspect-[4/3]">
              <img src={IMG.chaussantGallery[3]} alt="Savates Tahitian Move — collection signatures" className="w-full h-full object-cover" />
            </div>
            <div className="media-card col-span-6 md:col-span-6 aspect-[4/3]">
              <img src={IMG.chaussantGallery[4]} alt="Savates Tahitian Move — lifestyle plage" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Closing — invite à la boutique */}
      <section className="bg-paper py-24 md:py-32 border-t border-sand">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="eyebrow text-lagoon">Pas de vente en ligne</p>
          <p className="font-serif italic text-2xl md:text-3xl text-ink mt-4 leading-snug">
            « Pour découvrir l’ensemble des collections et leurs disponibilités, rendez-vous en boutique à Papeete. »
          </p>
          <Link to="/boutique" className="btn-primary mt-10 inline-flex">
            {t('common.visitBoutique')} <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}
